import { createEffect, createEvent, createStore, combine } from 'effector';
import { PaginationProps } from 'antd/lib/pagination';
import { get } from 'lodash';

import IUser, { IUserDevice } from '@interfaces/IUser';
import request from '@helpers/request';
import api from '@constants/endpoints';
import createPreloadersStore from './preloaders';

export const reset = createEvent('User devices reset');

interface GetUserDevicesParams {
  accountId: IUser['id'];
  pagination: PaginationProps;
}

interface UserDeviceResponse {
  id: number;
  udid: string;
}

const getAccountsCount = async (udid: string) => {
  const response = await request(api.users.getList, {
    params: {
      filterUdid: udid,
      page: 0,
      paged: true,
      size: 1,
    },
  });

  return Number(get(response, ['data', 'body', 'totalElements'], 0));
};

export const getList = createEffect({
  name: 'getUserDevices',
  async handler({ accountId, pagination }: GetUserDevicesParams): Promise<IUserDevice[]> {
    const response = await request(api.users.getDevices, {
      urlParams: { accountId },
      data: {
        pagination: {
          page: (pagination.current || 1) - 1,
          pageSize: pagination.pageSize || 10,
          type: 'PAGE',
        },
      },
    });

    const devices = get(response, ['data', 'body', 'devices'], []) as UserDeviceResponse[];
    const counts = await Promise.all(devices.map(({ udid }) => getAccountsCount(udid)));

    return devices.map((device, index) => ({
      ...device,
      accountsCount: counts[index],
    }));
  },
});

const preloaders = createPreloadersStore([getList]);

const list = createStore<IUserDevice[]>([])
  .on(getList.doneData, (_, devices) => devices)
  .reset(reset);

const userDevices = combine({ preloaders, list });

export default userDevices;
